import { NgTemplateOutlet } from '@angular/common';
import {
  Component,
  EventEmitter,
  Input,
  Output,
  ChangeDetectionStrategy
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { TranslateDirective } from '@ngx-translate/core';

import { BaseValueAccessor } from '../../../components/value-accessor/base-value-accessor';
import { ValueAccessorProviderFactory } from '../../../components/value-accessor/value-accessor-provider-factory';
import { IdService } from '../../../services/id.service';
import { DropdownComponent } from '../../../components/dropdown/dropdown.component';
import { TranslatePipe } from '../../../pipes/translate.pipe';
import { DropdownItemComponent } from '../../../components/dropdown/dropdown-item.component';
import { DropdownToggleDirective } from '../../../components/dropdown/dropdown-toggle.directive';
import { Account } from '../../../domain/public-api';
import { AccountLabelComponent } from './account-label.component';

@Component({
  selector: 'td-wb-account-selector',
  templateUrl: 'account-selector.component.html',
  styleUrls: ['account-selector.component.scss'],
  providers: [ValueAccessorProviderFactory(AccountSelectorComponent)],
  imports: [
    DropdownComponent,
    DropdownToggleDirective,
    TranslatePipe,
    TranslateDirective,
    FormsModule,
    NgTemplateOutlet,
    DropdownItemComponent,
    AccountLabelComponent
  ],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class AccountSelectorComponent extends BaseValueAccessor<Account> {

  public static readonly DEFAULT_DROP_DOWN_MAX_SIZE: number = 10;
  private static readonly ID_PREFIX: string = 'td-wb-dropdown-account-selector';

  uniqueId: string;
  @Input() accounts: Array<Account>;
  @Input() size: number = AccountSelectorComponent.DEFAULT_DROP_DOWN_MAX_SIZE;

  @Input()
  set showPlaceholder(value: boolean) {
    this._showPlaceholder = value;
  }

  get showPlaceholder(): boolean {
    return this._showPlaceholder;
  }

  @Input() disabled: boolean;

  @Input() account: Account;
  @Output() readonly accountChange: EventEmitter<Account> = new EventEmitter<Account>();
  private _showPlaceholder: boolean = false;

  constructor(private idService: IdService) {
    super();
    this.uniqueId = this.idService.generateNamespaceSequentialId(AccountSelectorComponent.ID_PREFIX);
  }

  onModelChange($event: Account): void {
    this.value = $event;
    this.accountChange.next($event);
  }

  comparator(key: keyof Account): (a: Record<string, unknown>, b: Record<string, unknown>) => boolean {
    return (a: Record<string, unknown>, b: Record<string, unknown>) => {
      const aDefined = a !== undefined && a !== null;
      const bDefined = b !== undefined && b !== null;
      const bothUndefined = (a === undefined && b === undefined);
      return (aDefined && bDefined && a[key] === b[key]) || bothUndefined;
    };
  }
}
